import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy as CopyIcon } from '@ankr.com/ui';
import { Typography } from '@mui/material';
import { TypographyProps } from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { copyText } from '../utils/copyText';

interface Props extends TypographyProps {
  text: string;
  children?: React.ReactNode;
  onCopy?: () => void;
}

export function Copy({
  children,
  text,
  className,
  onCopy,
  ...props
}: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const [copied, setCopied] = useState(false);
  const mountedRef = useRef(false);

  const handleCopyText = (): void => {
    setCopied(true);

    if (onCopy) {
      onCopy();
      return;
    }

    copyText(text);
  };

  useEffect(() => {
    if (!copied) {
      return;
    }

    setTimeout(() => {
      if (!mountedRef.current) {
        return;
      }

      setCopied(false);
    }, 3000);
  }, [copied]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <Typography {...props} className={cx(classes.container, className)}>
      {children}

      {copied ? (
        <Check
          sx={theme => ({ '&&': { color: theme.palette.success.main } })}
        />
      ) : (
        <CopyIcon className={classes.icon} onClick={handleCopyText} />
      )}
    </Typography>
  );
}

const useStyles = makeStyles()({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    '&&': {
      cursor: 'pointer',
    },
  },
});
